<template>
  <b-container fluid>
    <b-card
      border-variant="danger"
      :header="$t('EditItem.header')"
      header-bg-variant="danger"
      header-text-variant="white"
      align="left"
    >
      <div>
        <b-tabs content-class="mt-3" v-model="tabIndex">
          <b-tab v-for="(lang, i) in langOptions" :key="i">
            <template v-slot:title>
              <country-flag :country="getLocale(lang)" size="normal" />
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <div>
        <div v-for="(n, i) in form.name" :key="i">
          <b-row align-v="center" v-if="n.lang === selectedLanguage">
            <b-col>
              <b-form-group :label="$t('EditItem.name')">
                <b-form-input type="text" v-model="n.value" required></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </div>

      <div>
        <div v-for="(d, i) in form.description" :key="i">
          <b-row align-v="center" v-if="d.lang === selectedLanguage">
            <b-col>
              <b-form-group :label="$t('EditItem.description')">
                <b-form-input type="text" v-model="d.value"></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </div>

      <b-row>
        <b-col v-if="form.image" cols="*" sm="6">
          <b-form-group :label="$t('EditItem.currentImage')">
            <b-img :src="form.image" fluid />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group :label="$t('EditItem.uploadImage')">
            <b-form-file
              v-model="selectedFile"
              :state="Boolean(selectedFile)"
              @change="onFileSelected"
              :placeholder="uploadIcon"
            ></b-form-file>
            <b-progress
              v-if="uploadProgress.counter && uploadProgress.counter !== 100"
              :value="uploadProgress.counter"
              :max="uploadProgress.max"
              show-progress
              animated
            ></b-progress>
          </b-form-group>
        </b-col>
        <b-col v-if="form.image" class="text-right">
          <b-form-group :label="$t('EditItem.deleteImageTitle')">
            <b-button
              size="md"
              left
              @click="form.image = null"
              variant="danger"
              class="float-right"
            >{{$t('EditItem.deleteImage')}}</b-button>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row align-v="center">
        <b-col>
          <b-form-group :label="$t('EditItem.category')">
            <b-form-select v-model="form.category" :options="categoryOptions" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row align-v="center">
        <b-col cols="*" sm="6">
          <b-form-group :label="$t('EditItem.price')">
            <b-form-input :disabled="disablePrice" type="text" v-model="form.price"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="*" sm="6">
          <b-form-group :label="$t('EditItem.currency')">
            <b-form-select v-model="form.currency" :options="currencyOptions" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row align-v="center">
        <b-col cols="*" sm="6">
          <b-form-group :label="$t('EditItem.placeNumber')">
            <b-form-input type="text" v-model="form.placeNumber"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="*" sm="6">
          <b-form-group :label="$t('EditItem.status')">
            <b-form-checkbox
              v-model="form.hidden"
              value="true"
              unchecked-value="false"
            >{{ $t('EditItem.hidden') }}</b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group v-if="form.variants.length > 0" :label="$t('EditItem.variants')">
        <div v-for="(variant, i) in form.variants" :key="i">
          <div v-for="(name, j) in variant.name" :key="j">
            <div v-if="name.lang === selectedLanguage">
              <b-row align-v="center" class="variants">
                <b-col cols="*" sm="5">
                  <b-form-input
                    type="text"
                    :placeholder="$t('EditItem.variantsName')"
                    v-model="name.value"
                  ></b-form-input>
                </b-col>
                <b-col cols="*" sm="5">
                  <b-form-input
                    type="text"
                    :placeholder="$t('EditItem.variantsPrice')"
                    v-model="variant.price"
                  ></b-form-input>
                </b-col>
                <b-col cols="*" sm="2">
                  <b-button
                    size="sm"
                    left
                    @click="removeVariant(variant)"
                    variant="danger"
                    class="float-right"
                  >
                    <font-awesome-icon icon="minus" />
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </b-form-group>

      <b-form-group v-if="form.extras.length > 0" :label="$t('EditItem.extras')">
        <div v-for="(extra, i) in form.extras" :key="i">
          <div v-for="(name, j) in extra.name" :key="j">
            <div v-if="name.lang === selectedLanguage">
              <b-row align-v="center" class="extras">
                <b-col cols="*" sm="5">
                  <b-form-input
                    type="text"
                    :placeholder="$t('EditItem.extrasName')"
                    v-model="name.value"
                  ></b-form-input>
                </b-col>
                <b-col cols="*" sm="5">
                  <b-form-input
                    type="text"
                    :placeholder="$t('EditItem.extrasPrice')"
                    v-model="extra.price"
                  ></b-form-input>
                </b-col>
                <b-col cols="*" sm="2">
                  <b-button
                    size="sm"
                    left
                    @click="removeExtra(extra)"
                    variant="danger"
                    class="float-right"
                  >
                    <font-awesome-icon icon="minus" />
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </b-form-group>

      <b-form-group v-if="form.styles.length > 0" :label="$t('EditItem.styles')">
        <div v-for="(style, i) in form.styles" :key="i">
          <div v-for="(name, j) in style.name" :key="j">
            <div v-if="name.lang === selectedLanguage">
              <b-row align-v="center" class="styles">
                <b-col cols="*" sm="10">
                  <b-form-input
                    type="text"
                    :placeholder="$t('EditItem.stylePlaceholder')"
                    v-model="name.value"
                  ></b-form-input>
                </b-col>
                <b-col cols="*" sm="2">
                  <b-button
                    size="sm"
                    left
                    @click="removeStyle(style)"
                    variant="danger"
                    class="float-right"
                  >
                    <font-awesome-icon icon="minus" />
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </b-form-group>

      <b-form-group v-if="form.tags.length > 0" :label="$t('EditItem.tags')">
        <b-row align-v="center" class="tags">
          <b-col cols="*" offset="1" v-for="(tagId, i) in form.tags" :key="i">
            <div v-for="(tag, j) in unfilteredFilterTags" :key="j">
              <div v-if="tag._id === tagId">
                <h5>
                  <b-badge variant="light">
                    {{filterLang(tag)}}
                    <b-button size="sm" variant="danger" @click="removeTag(tagId)">
                      <font-awesome-icon icon="minus" />
                    </b-button>
                  </b-badge>
                </h5>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-form-group>

      <div>
        <b-tabs content-class="mt-3" v-model="tabIndex">
          <b-tab v-for="(lang, i) in langOptions" :key="i">
            <template v-slot:title>
              <country-flag :country="getLocale(lang)" size="normal" />
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <b-form-group :label="$t('EditItem.addVariant')">
        <div v-for="(n,i) in newVariant.name" :key="i">
          <b-row align-v="center" class="variants" v-if="n.lang === selectedLanguage">
            <b-col cols="*" sm="5">
              <b-form-input
                type="text"
                :placeholder="$t('EditItem.addVariantName')"
                v-model="n.value"
                :disabled="disableVariant"
              ></b-form-input>
            </b-col>
            <b-col cols="*" sm="5">
              <b-form-input
                type="text"
                :placeholder="$t('EditItem.addVariantPrice')"
                v-model="newVariant.price"
                :disabled="disableVariant"
              ></b-form-input>
            </b-col>
            <b-col cols="*" sm="2">
              <b-button size="sm" left @click="addToVariant()" variant="info" class="float-right">
                <font-awesome-icon icon="plus" />
              </b-button>
            </b-col>
          </b-row>
        </div>
      </b-form-group>

      <b-form-group :label="$t('EditItem.addExtra')">
        <div v-for="(n,i) in newExtra.name" :key="i">
          <b-row align-v="center" class="extras" v-if="n.lang === selectedLanguage">
            <b-col cols="*" sm="5">
              <b-form-input
                type="text"
                :placeholder="$t('EditItem.addExtraName')"
                v-model="n.value"
              ></b-form-input>
            </b-col>
            <b-col cols="*" sm="5">
              <b-form-input
                type="text"
                :placeholder="$t('EditItem.addExtraPrice')"
                v-model="newExtra.price"
              ></b-form-input>
            </b-col>
            <b-col cols="*" sm="2">
              <b-button size="sm" left @click="addToExtra()" variant="info" class="float-right">
                <font-awesome-icon icon="plus" />
              </b-button>
            </b-col>
          </b-row>
        </div>
      </b-form-group>

      <b-form-group :label="$t('EditItem.addStyle')" v-if="newStyle">
        <div v-for="(n,i) in newStyle.name" :key="i">
          <b-row align-v="center" class="styles" v-if="n.lang === selectedLanguage">
            <b-col cols="*" sm="10">
              <b-form-input
                type="text"
                :placeholder="$t('EditItem.addStylePlaceholder')"
                v-model="n.value"
              ></b-form-input>
            </b-col>
            <b-col cols="*" sm="2">
              <b-button size="sm" left @click="addToStyle()" variant="info" class="float-right">
                <font-awesome-icon icon="plus" />
              </b-button>
            </b-col>
          </b-row>
        </div>
      </b-form-group>

      <b-row align-v="center">
        <b-col cols="*" sm="10">
          <b-form-group :label="$t('EditItem.addTag')">
            <b-form-select v-model="filterTagSelected" :options="ftOptions" />
          </b-form-group>
        </b-col>
        <b-col cols="*" sm="2">
          <b-button
            size="sm"
            left
            @click="addToTag(filterTagSelected)"
            variant="info"
            class="float-right"
          >
            <font-awesome-icon icon="plus" />
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mt-3 justify-content-center">
        <b-col>
          <b-button
            variant="secondary"
            type="submit"
            class="float-right"
            @click="$router.push({name: 'EditItems'})"
          >{{ $t('EditItem.cancel') }}</b-button>
        </b-col>
        <b-col>
          <b-button
          variant="danger"
          type="submit"
          @click="checkFieldsAndSave"
        >{{ $t('EditItem.save') }}</b-button>
        </b-col>
      </b-row>
      <b-row class="mt-3 justify-content-center">
        <b-alert :show="error.received" variant="danger" class="text-center">{{error.msg}}</b-alert>
      </b-row>
      <b-row v-if="error.showLink" class="justify-content-center">
        <router-link :to="{name: 'EditItems'}">{{ $t('EditItem.editItemsLink') }}</router-link>
      </b-row>
      <div></div>
    </b-card>
  </b-container>
</template>

<script>
/* CONTROLLER */
import locales from '../i18n/locales'
import ItemsService from '@/services/ItemsService'
import CategoriesService from '@/services/CategoriesService'
import { mapGetters } from 'vuex'
import ImagesService from '@/services/ImagesService'
import FilterTagsService from '@/services/FilterTagsService'

export default {
  name: 'EditCategory',
  data () {
    return {
      categories: [],
      categoryOptions: [
        { value: null, text: this.$i18n.t('EditItem.categoryOptions') }
      ],
      currencyOptions: [
        { value: null, text: this.$i18n.t('EditItem.currencyOptions') },
        'CHF',
        'EUR',
        'USD'
      ],
      disablePrice: false,
      disableVariant: false,
      error: {
        received: false,
        msg: '',
        showLink: false
      },
      filterTags: [],
      filterTagsOptions: [
        { value: null, text: this.$i18n.t('EditItem.filterTagsOptions') }
      ],
      filterTagSelected: null,
      form: {
        _id: null,
        name: null,
        description: null,
        image: null,
        hidden: null,
        category: null,
        currency: null,
        placeNumber: null,
        price: null,
        styles: [],
        tags: [],
        extras: [],
        variants: []
      },
      langOptions: [],
      newExtra: {
        name: null,
        price: null
      },
      newStyle: null,
      newVariant: {
        name: null,
        price: null
      },
      selectedFile: null,
      selectedLanguage: this.$i18n.locale,
      tabIndex: 0,
      unfilteredCategories: [],
      unfilteredFilterTags: [],
      uploadIcon: '\u21EA',
      uploadProgress: {
        counter: null,
        max: 100
      }
    }
  },
  mounted () {
    // redirect if user not logged in
    let smartdineLocalStorage = JSON.parse(
      window.localStorage.getItem('smartdine')
    )
    if (!smartdineLocalStorage.token) this.$router.push({ name: 'Login' })
    // if currentItem not in state, redirect back to EditItems
    else if (!this.currentItem) this.$router.push({ name: 'EditItems' })
    // if all good, fetch item
    else {
      this.fetchItem()
      this.fetchCategories()
      this.fetchFilterTags()
      this.languageOptions()
      this.selectedLanguage = this.langOptions[this.tabIndex].value
      this.buildAddObjects()
    }
  },
  methods: {
    onFileSelected (event) {
      this.selectedFile = event.target.files[0]
    },
    async fetchItem () {
      const item = this.currentItem

      // Prefill form
      this.form._id = item._id
      this.form.hidden = item.hidden
      this.form.category = item.category
      this.form.currency = item.currency
      this.form.placeNumber = item.placeNumber
      this.form.price = item.price
      this.form.image = item.image
      this.form.name = item.name
      this.form.description = item.description
      this.form.variants = item.variants
      this.form.extras = item.extras
      this.form.styles = item.styles
      this.form.tags = item.tags

      // create null values for any empty names
      Object.keys(this.$i18n.messages).forEach(l => {
        let langExists = item.name.some(n => {
          return n.lang === l
        })
        if (!langExists) this.form.name.push({ lang: l, value: null })
      })

      // create null values for any empty descriptions
      if (item.description.length > 0) {
        Object.keys(this.$i18n.messages).forEach(l => {
          let langExists = item.description.some(d => {
            return d.lang === l
          })
          if (!langExists) this.form.description.push({ lang: l, value: null })
        })
      } else {
        Object.keys(this.$i18n.messages).forEach(l => {
          this.form.description.push({ lang: l, value: null })
        })
      }

      // create null values only for the following objects that already have data
      if (item.styles.length > 0) {
        item.styles.forEach((style, i) => {
          Object.keys(this.$i18n.messages).forEach(l => {
            let langExists = style.name.some(n => {
              return n.lang === l
            })
            if (!langExists) {
              this.form.styles[i].name.push({ lang: l, value: null })
            }
          })
        })
      }
      if (item.extras.length > 0) {
        item.extras.forEach((extra, i) => {
          Object.keys(this.$i18n.messages).forEach(l => {
            let langExists = extra.name.some(n => {
              return n.lang === l
            })
            if (!langExists) {
              this.form.extras[i].name.push({ lang: l, value: null })
            }
          })
        })
      }
      if (item.variants.length > 0) {
        item.variants.forEach((variant, i) => {
          Object.keys(this.$i18n.messages).forEach(l => {
            let langExists = variant.name.some(n => {
              return n.lang === l
            })
            if (!langExists) {
              this.form.variants[i].name.push({ lang: l, value: null })
            }
          })
        })
      }
    },
    async fetchCategories () {
      // get all categories
      try {
        const response = await CategoriesService.getCategories()
        this.unfilteredCategories = response.data.categories

        this.filterCategories()
      } catch (error) {
        console.log(error)
      }
    },
    async fetchFilterTags () {
      // get all fetchFilterTags
      try {
        const response = await FilterTagsService.getFilterTags()
        this.unfilteredFilterTags = response.data.filtertags
        this.filterFilterTags()
      } catch (error) {
        console.log(error)
      }
    },
    filterCategories () {
      // clear out categories array if not empty
      if (this.categories.length > 0) {
        this.categories = []
      }

      // filter based on current language
      // and assign to categories
      this.unfilteredCategories.forEach(category => {
        let localeCategory = {
          _id: category._id,
          title: this.filterLang(category),
          parent: category.parent || null,
          children: category.children || [],
          hidden: category.hidden,
          placeNumber: category.placeNumber || null
        }
        this.categories.push(localeCategory)
      })

      if (this.categoryOptions.length > 1) {
        this.categoryOptions = []
        this.categoryOptions.push({
          value: null,
          text: this.$i18n.t('AddItem.categorySelectOption')
        })
      }

      // populate drop-down list
      this.categories.forEach(category => {
        let dplCategory = {
          text: category.title,
          value: category._id
        }

        this.categoryOptions.push(dplCategory)
      })
    },
    filterFilterTags () {
      // clear out filterTags array if not empty
      if (this.filterTags.length > 0) {
        this.filterTags = []
      }

      // filter based on current language
      // and assign to categories
      this.unfilteredFilterTags.forEach(filterTag => {
        let localeFilterTag = {
          _id: filterTag._id,
          name: this.filterLang(filterTag)
        }
        this.filterTags.push(localeFilterTag)
      })

      if (this.filterTagsOptions.length > 1) {
        this.filterTagsOptions = []
        this.filterTagsOptions.push({
          value: null,
          text: this.$i18n.t('EditItem.filterTagsOptions')
        })
      }

      // populate drop-down list
      this.filterTags.forEach(filterTag => {
        let dplFilterTag = {
          text: filterTag.name,
          value: filterTag._id
        }

        this.filterTagsOptions.push(dplFilterTag)
      })
    },
    buildAddObjects () {
      let styleName = []
      let variantName = []
      let extraName = []

      Object.keys(this.$i18n.messages).forEach(l => {
        styleName.push({ lang: l, value: null })
        variantName.push({ lang: l, value: null })
        extraName.push({ lang: l, value: null })
      })

      // asign to newStyle
      this.newStyle = {
        name: styleName
      }

      // asign to newVariant
      this.newVariant = {
        name: variantName,
        price: null
      }

      // asign to newExtra
      this.newExtra = {
        name: extraName,
        price: null
      }
    },
    addToVariant () {
      // make sure that at there's at least one name value
      let checkValue = this.newVariant.name.some(n => {
        return n.value !== null
      })
      if (!checkValue) {
        this.error.received = true
        this.error.msg = this.$i18n.t('EditItem.addToVarientErrorMsg')
      } else {
        const newVariant = {
          name: this.newVariant.name,
          price: this.newVariant.price
        }
        // add to form.variants array
        this.form.variants.push(newVariant)

        // reset newVariant
        let variantName = []
        Object.keys(this.$i18n.messages).forEach(l => {
          variantName.push({ lang: l, value: null })
        })
        this.newVariant = {
          name: variantName,
          price: null
        }
      }
    },
    removeVariant (variant) {
      // take out clicked object
      const updatedVariant = this.form.variants.filter(v => {
        return v !== variant
      })
      // update form.variants array
      this.form.variants = updatedVariant
    },
    addToExtra () {
      // make sure that at there's at least one name value
      let checkValue = this.newExtra.name.some(n => {
        return n.value !== null
      })
      if (!checkValue) {
        this.error.received = true
        this.error.msg = this.$i18n.t('EditItem.addToExtraErrorMsg')
      } else {
        const newExtra = {
          name: this.newExtra.name,
          price: this.newExtra.price
        }
        // add to form.extras array
        this.form.extras.push(newExtra)

        // reset newVariant
        let extraName = []
        Object.keys(this.$i18n.messages).forEach(l => {
          extraName.push({ lang: l, value: null })
        })
        this.newExtra = {
          name: extraName,
          price: null
        }
      }
    },
    removeExtra (extra) {
      // take out clicked object
      const updatedExtra = this.form.extras.filter(e => {
        return e !== extra
      })

      // update form.extras array
      this.form.extras = updatedExtra
    },
    addToStyle () {
      const newStyle = this.newStyle
      this.form.styles.push(newStyle)

      // reset newStyle
      let styleName = []
      Object.keys(this.$i18n.messages).forEach(l => {
        styleName.push({ lang: l, value: null })
      })
      this.newStyle = {
        name: styleName
      }
    },
    removeStyle (style) {
      // take out clicked object
      const updatedStyle = this.form.styles.filter(s => {
        return s !== style
      })

      // update form.extras array
      this.form.styles = updatedStyle
    },
    addToTag (filterTag) {
      this.form.tags.push(filterTag)
    },
    removeTag (tagId) {
      // take out clicked object
      const updatedTag = this.form.tags.filter(t => {
        return t !== tagId
      })

      // update form.extras array
      this.form.tags = updatedTag
    },
    async saveItem () {
      // if new picture file was selected
      if (this.selectedFile) {
        const fd = new FormData()
        fd.append('image', this.selectedFile, this.selectedFile.name)

        try {
          const upload = await ImagesService.upload(fd, {
            onUploadProgress: uploadEvent => {
              this.uploadProgress.counter = Math.round(
                (uploadEvent.loaded / uploadEvent.total) * 100
              )
            }
          })
          // add link to image
          this.form.image = upload.data.file

          // save item
          try {
            await ItemsService.updateItem(this.form)

            // navigate back to EditItems
            this.$router.push({ name: 'EditItems' })
          } catch (error) {
            this.error.received = true
            this.error.msg = error.response.data.error
          }
        } catch (error) {
          this.error.received = true
          this.error.msg = error.response.data.err
        }
      } else {
        try {
          await ItemsService.updateItem(this.form)

          // navigate back to EditItems
          this.$router.push({ name: 'EditItems' })
        } catch (error) {
          this.error.received = true
          this.error.msg = error.response.data.error
        }
      }
    },
    checkFieldsAndSave () {
      // take out any null lang/value objects
      this.form.name = this.filterNulls(this.form.name)
      this.form.description = this.filterNulls(this.form.description)
      // styles
      let filteredStyles = []
      this.form.styles.forEach(s => {
        let name = this.filterNulls(s.name)
        filteredStyles.push({ name: name })
      })
      this.form.styles = filteredStyles
      // extras
      let filteredExtras = []
      this.form.extras.forEach(e => {
        let name = this.filterNulls(e.name)
        filteredExtras.push({ name: name, price: e.price })
      })
      this.form.extras = filteredExtras
      // variants
      let filteredVariants = []
      this.form.variants.forEach(v => {
        let name = this.filterNulls(v.name)
        filteredVariants.push({
          name: name,
          price: v.price
        })
      })
      this.form.variants = filteredVariants

      if (!!this.form.name && !!this.form.price) {
        this.saveItem()
      } else if (!!this.form.name && this.form.variants.length > 0) {
        this.saveItem()
      } else {
        this.error.received = true
        this.error.msg = this.$i18n.t('EditItem.serverMessages.saveErrorMsg')
      }
    },
    languageOptions () {
      // get languages from i18n and assign value/text to langOptions
      this.langOptions = Object.keys(this.$i18n.messages).map(l => {
        return { value: l, text: l.toUpperCase() }
      })
    },
    filterLang (category) {
      if (category.title) {
        // filter for selected language
        let title = category.title.filter(t => {
          return t.lang === this.selectedLanguage
        })
        // if title value undefined, filter for and assign a defined value
        return title.length > 0
          ? title[0].value
          : category.title.filter(t => {
            return t.value.length > 0
          })[0].value
      }
      if (category.name) {
        // filter for selected language
        let name = category.name.filter(n => {
          return n.lang === this.selectedLanguage
        })
        // if title value undefined, filter for and assign a defined value
        return name.length > 0
          ? name[0].value
          : category.name.filter(n => {
            return n.value.length > 0
          })[0].value
      }
    },
    filterNulls (obj) {
      return obj.filter(el => {
        return el.value !== null && el.value !== ''
      })
    },
    getLocale (lang) {
      return locales.filter(l => l.code === lang.value)[0].flag
    }
  },
  computed: {
    ...mapGetters({ currentItem: 'currentItem' }),
    ftOptions () {
      return this.filterTagsOptions.filter(t => {
        return !this.form.tags.includes(t.value)
      })
    }
  },
  watch: {
    'form.price': {
      handler: function () {
        if (this.form.price !== null && this.form.price !== '') {
          this.form.variants = []
          this.disableVariant = true
        } else this.disableVariant = false
      },
      deep: true
    },
    'form.variants': {
      handler: function () {
        if (this.form.variants.length > 0) {
          this.form.price = null
          this.disablePrice = true
        } else {
          this.disablePrice = false
        }
      },
      deep: true
    },
    tabIndex () {
      if (this.tabIndex !== -1) {
        this.selectedLanguage = this.langOptions[this.tabIndex].value
        this.filterCategories()
        this.filterFilterTags()
      }
    }
  }
}
</script>

<style scoped>
.btn {
  margin: 0.1em 0.25em 0.1em 0.25em;
}

.modal-dialog h3 {
  color: #0d47a1;
}
.badge {
  line-height: 1.5em;
}
.variants {
  margin-bottom: 1em;
}

.badge-light {
    color: #212529;
    background-color:#e9ecef;
}
</style>



// WEBPACK FOOTER //
// src/components/EditItem.vue